import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import AuthModal from '@/components/auth/AuthModal';
import { Sparkles, Menu, X, User, LogOut, Image, LayoutGrid } from 'lucide-react';

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/editor', label: 'Create', icon: Sparkles },
    { path: '/templates', label: 'Templates', icon: LayoutGrid },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header className="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-2 group"
              data-testid="logo-link"
            >
              <div className="w-8 h-8 bg-[#bef264] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <Sparkles className="w-5 h-5 text-black" />
              </div>
              <span className="text-xl font-bold text-white font-['Space_Grotesk']">
                MemeStudio
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  data-testid={`nav-${label.toLowerCase()}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(path)
                      ? 'bg-[#bef264]/10 text-[#bef264]'
                      : 'text-[#a1a1aa] hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </Link>
              ))}
              {isAuthenticated && (
                <Link
                  to="/gallery"
                  data-testid="nav-gallery"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive('/gallery')
                      ? 'bg-[#bef264]/10 text-[#bef264]'
                      : 'text-[#a1a1aa] hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Image className="w-4 h-4" />
                  My Gallery
                </Link>
              )}
            </nav>

            {/* Auth Section */}
            <div className="hidden md:flex items-center gap-3">
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 text-[#a1a1aa] hover:text-white"
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-8 h-8 bg-[#27272a] rounded-full flex items-center justify-center">
                        <User className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">{user?.username}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-[#18181b] border-[#27272a]">
                    <DropdownMenuItem asChild>
                      <Link to="/gallery" className="flex items-center gap-2 cursor-pointer">
                        <Image className="w-4 h-4" />
                        My Gallery
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#27272a]" />
                    <DropdownMenuItem 
                      onClick={logout}
                      className="flex items-center gap-2 cursor-pointer text-red-400 focus:text-red-400"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  onClick={() => setShowAuthModal(true)}
                  className="bg-[#bef264] text-black hover:bg-[#a3e635] font-medium"
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-[#a1a1aa] hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#27272a] bg-[#09090b]">
            <div className="px-4 py-3 space-y-1">
              {navLinks.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive(path)
                      ? 'bg-[#bef264]/10 text-[#bef264]'
                      : 'text-[#a1a1aa] hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {label}
                </Link>
              ))}
              {isAuthenticated && (
                <Link
                  to="/gallery"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive('/gallery')
                      ? 'bg-[#bef264]/10 text-[#bef264]'
                      : 'text-[#a1a1aa] hover:bg-white/5'
                  }`}
                >
                  <Image className="w-5 h-5" />
                  My Gallery
                </Link>
              )}
              <div className="pt-3 border-t border-[#27272a]">
                {isAuthenticated ? (
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-400 w-full"
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                ) : (
                  <Button
                    onClick={() => {
                      setShowAuthModal(true);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-[#bef264] text-black hover:bg-[#a3e635] font-medium"
                  >
                    Sign In
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </>
  );
};

export default Header;
